import {
  BottomGraphic,
  ContactUs,
  FadeIn,
  Footer,
  GradientBg,
  Header,
  WidgetExample,
} from "../components"
import {
  ArrowRightIcon,
  ArrowsExpandIcon,
  CashIcon,
  ChartPieIcon,
  ClipboardCheckIcon,
  CodeIcon,
  ColorSwatchIcon,
  CreditCardIcon,
  CurrencyDollarIcon,
  DownloadIcon,
  ExternalLinkIcon,
  LinkIcon,
  ShoppingCartIcon,
  TerminalIcon,
  UserGroupIcon,
} from "@heroicons/react/outline"
import type { NextPage } from "next"
import { NextSeo } from "next-seo"
import Link from "next/link"
import { useRouter } from "next/router"

const embedSteps = [
  {
    title: "Install the Coverdash component from NPM",
    icon: DownloadIcon,
  },
  {
    title: "Add the component with a single line of code",
    icon: CodeIcon,
  },
  {
    title:
      "Provide affordable insurance to your customers by start of business tomorrow",
    icon: UserGroupIcon,
  },
  {
    title: "Unlock ancillary insurance revenue stream",
    icon: CurrencyDollarIcon,
  },
]

const integrationSteps = [
  {
    title: `Insurance experience hosted in partner's own native web environment`,
    icon: LinkIcon,
  },
  {
    title: "No redirection of customers to Coverdash platform",
    icon: ExternalLinkIcon,
  },
  {
    title: "End-to-end embedded transactional experience",
    icon: CashIcon,
  },
]

const transactionalSteps = [
  {
    title: "Direct API connectivity with our leading carrier panel",
    icon: TerminalIcon,
  },
  {
    title: "Industry leading insurance pricing for your customer base",
    icon: ChartPieIcon,
  },
  {
    title: "End-to-end transactional experience including quote, rate and bind",
    icon: ShoppingCartIcon,
  },
  {
    title: "Proprietary customer digital payment collection",
    icon: CreditCardIcon,
  },
]

const brandSteps = [
  {
    title: "Pre-packaged or fully customizable UI style sheets",
    icon: ColorSwatchIcon,
  },
  {
    title: "Compliant user experience",
    icon: ClipboardCheckIcon,
  },
  {
    title: "Broaden your existing customer journey",
    icon: ArrowsExpandIcon,
  },
]

const Partner: NextPage = () => {
  const router = useRouter()
  const canonicalUrl = (
    `https://www.coverdash.com` + (router.asPath === "/" ? "" : router.asPath)
  ).split("?")[0]
  return (
    <>
      <NextSeo
        title="Partner with us"
        description="Embed business insurance in your platform with one line of code. Offer quote-to-bind coverage, top carriers, and new revenue—no customer redirects. — Coverdash"
        canonical={canonicalUrl}
        openGraph={{
          url: canonicalUrl,
          title: "Partner with us",
          description:
            "Embed business insurance in your platform with one line of code. Offer quote-to-bind coverage, top carriers, and new revenue—no customer redirects. — Coverdash",
        }}
      />
      <GradientBg />
      <Header />

      <div className="h-screen flex flex-col justify-center">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <FadeIn>
              <h1 className="text-4xl tracking-tight font-extrabold sm:text-5xl md:text-6xl text-primary">
                Partner with us
              </h1>
            </FadeIn>
            <div className="mx-auto max-w-md px-4 sm:max-w-2xl sm:px-6 lg:max-w-xl lg:px-0 pt-16 justify-center">
              <FadeIn delay={0.2}>
                <img src="/img/partner.svg" className="w-full" alt="" />
              </FadeIn>
            </div>
            <p className="mt-4 max-w-md mx-auto text-base text-gray-600 sm:text-lg md:mt-8 md:text-xl md:max-w-3xl">
              Embed our business insurance experience into your platform with
              ease.
            </p>
            <div className="mt-8 sm:mt-12 space-y-4">
              <FadeIn delay={0.2}>
                <Link legacyBehavior href="#contact-us" scroll>
                  <a className="btn-primary">
                    <span>Become a Partner</span>
                    <ArrowRightIcon className="h-4 w-4 inline" />
                  </a>
                </Link>
              </FadeIn>
            </div>
          </div>
        </div>
      </div>

      {/* <div className="relative overflow-hidden">
        <div className="pt-10 sm:pt-16 lg:pt-8 lg:pb-14 my-16">
          <div className="mx-auto max-w-7xl lg:px-8">
            <div className="md:grid md:grid-cols-2 md:gap-8">
              <div className="mx-auto max-w-md px-4 sm:max-w-2xl sm:px-6 lg:px-0 lg:text-left lg:flex lg:items-center">
                <div className="lg:py-24">
                  <FadeIn>
                    <div className="mt-3 sm:max-w-xl">
                      <h1 className="text-4xl font-extrabold text-alt tracking-tight sm:text-5xl">
                        Partner with us
                      </h1>
                      <p className="mt-6 text-xl text-gray-500">
                        Embed our small business insurance experience into your
                        platform with ease
                      </p>
                    </div>
                  </FadeIn>
                  <div className="mt-8 sm:mt-12 space-y-4">
                    <FadeIn delay={0.2}>
                      <Link href="#contact-us" scroll>
                        <a className="btn-primary">
                          <span>Become a Partner</span>
                          <ArrowRightIcon className="h-4 w-4 inline" />
                        </a>
                      </Link>
                    </FadeIn>
                  </div>
                </div>
              </div>
              <div className="mt-12 lg:m-0 lg:relative self-end flex">
                <div className="mx-auto max-w-md px-4 sm:max-w-2xl sm:px-6 lg:max-w-none lg:px-0 justify-center">
                  <FadeIn delay={0.2}>
                    <img src="/img/partner.svg" className="w-full" alt="" />
                  </FadeIn>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div> */}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-16">
        <FadeIn>
          <h2 className="text-5xl text-alt font-extrabold my-7 text-center max-w-5xl mx-auto">
            Simplified <span className="text-primary">insurance solution</span>{" "}
            for your customers
          </h2>
        </FadeIn>
        <div className="max-w-xl mx-auto text-xl text-gray-500 text-center">
          <p className="mt-5">
            Coverdash enables your platform with the capability to provide
            quick, simplified and affordable business insurance coverage to your
            customers
          </p>
          <p className="mt-5">See what Coverdash can do for you:</p>
        </div>
      </div>

      <div className="px-8 max-w-7xl mx-auto py-16">
        <div className="relative lg:grid lg:grid-cols-5 lg:items-center lg:gap-16 max-w-3xl lg:max-w-none mx-auto">
          <div className="col-span-2">
            <FadeIn>
              <h3 className="text-2xl font-extrabold text-alt sm:text-3xl">
                Embed with a single line of code
              </h3>
              <dl className="mt-10 space-y-10">
                {embedSteps.map((item) => (
                  <div key={item.title}>
                    <dt className="flex flex-col h-full justify-center">
                      <div className="absolute flex h-10 w-10 items-center justify-center rounded-md bg-primary text-white">
                        <item.icon className="h-6 w-6" aria-hidden="true" />
                      </div>
                      <p className="ml-16 text-lg font-medium leading-6 text-alt">
                        {item.title}
                      </p>
                    </dt>
                  </div>
                ))}
              </dl>
            </FadeIn>
          </div>

          <div className="col-span-3 mt-16 lg:mt-0" aria-hidden="true">
            <FadeIn delay={0.2}>
              <WidgetExample />
            </FadeIn>
          </div>
        </div>
      </div>

      <div className="px-8 max-w-7xl mx-auto py-16">
        <div className="relative grid grid-cols-1 lg:grid-cols-5 lg:items-center lg:gap-16 max-w-3xl lg:max-w-none mx-auto">
          <div
            className="col-span-3 order-last lg:order-first pt-16 lg:pt-0"
            aria-hidden="true"
          >
            <FadeIn delay={0.2}>
              <img
                src="/img/embeded-website.svg"
                className="h-auto md:h-full w-auto rounded-lg shadow-xl"
                aria-hidden="true"
                alt="Code widget on website"
              />
            </FadeIn>
          </div>

          <div className="col-span-2">
            <FadeIn>
              <h3 className="text-2xl font-extrabold text-alt sm:text-3xl">
                Fully embedded integration
              </h3>
              <dl className="mt-10 space-y-10">
                {integrationSteps.map((item) => (
                  <div key={item.title}>
                    <dt className="flex flex-col h-full justify-center">
                      <div className="absolute flex h-10 w-10 items-center justify-center rounded-md bg-primary text-white">
                        <item.icon className="h-6 w-6" aria-hidden="true" />
                      </div>
                      <p className="ml-16 text-lg font-medium leading-6 text-alt">
                        {item.title}
                      </p>
                    </dt>
                  </div>
                ))}
              </dl>
            </FadeIn>
          </div>
        </div>
      </div>

      <div className="px-8 max-w-7xl mx-auto py-16">
        <div className="relative grid grid-cols-1 lg:grid-cols-5 lg:items-center lg:gap-16 max-w-3xl lg:max-w-none mx-auto">
          <div className="col-span-2">
            <FadeIn>
              <h3 className="text-2xl font-extrabold text-alt sm:text-3xl">
                Frictionless transactional processing
              </h3>
              <dl className="mt-10 space-y-10">
                {transactionalSteps.map((item) => (
                  <div key={item.title}>
                    <dt className="flex flex-col h-full justify-center">
                      <div className="absolute flex h-10 w-10 items-center justify-center rounded-md bg-primary text-white">
                        <item.icon className="h-6 w-6" aria-hidden="true" />
                      </div>
                      <p className="ml-16 text-lg font-medium leading-6 text-alt">
                        {item.title}
                      </p>
                    </dt>
                  </div>
                ))}
              </dl>
            </FadeIn>
          </div>

          <div className="col-span-3 pt-10 lg:pt-0" aria-hidden="true">
            <FadeIn delay={0.2}>
              <img
                src="/img/congrats.svg"
                className="h-auto md:h-full w-auto rounded-lg shadow-xl"
                aria-hidden="true"
                alt="Quote success message"
              />
            </FadeIn>
          </div>
        </div>
      </div>

      <div className="px-8 max-w-7xl mx-auto py-16">
        <div className="relative grid grid-cols-1 lg:grid-cols-5 lg:items-center lg:gap-16 max-w-3xl lg:max-w-none mx-auto">
          <div className="col-span-3 pt-10 lg:pt-0" aria-hidden="true">
            <FadeIn delay={0.2}>
              <img
                src="/img/embeded-branding.svg"
                className="h-auto md:h-full w-auto mx-auto"
                aria-hidden="true"
                alt="Widget in different styles"
              />
            </FadeIn>
          </div>

          <div className="col-span-2 order-first lg:order-last">
            <FadeIn>
              <h3 className="text-2xl font-extrabold text-alt sm:text-3xl">
                Brand experience
              </h3>
              <dl className="mt-10 space-y-10">
                {brandSteps.map((item) => (
                  <div key={item.title}>
                    <dt className="flex flex-col h-full justify-center">
                      <div className="absolute flex h-10 w-10 items-center justify-center rounded-md bg-primary text-white">
                        <item.icon className="h-6 w-6" aria-hidden="true" />
                      </div>
                      <p className="ml-16 text-lg font-medium leading-6 text-alt">
                        {item.title}
                      </p>
                    </dt>
                  </div>
                ))}
              </dl>
            </FadeIn>
          </div>
        </div>
      </div>

      <div className="px-8 max-w-7xl mx-auto py-16">
        <div className="relative lg:grid lg:grid-cols-5 lg:items-center lg:gap-16 max-w-3xl lg:max-w-none mx-auto">
          <div className="col-span-2">
            <FadeIn>
              <h3 className="text-2xl font-extrabold text-alt sm:text-3xl">
                24/7 customer communication with our support staff
              </h3>
              <p className="mt-3 text-lg text-gray-500">
                Coverdash's team of insurance experts are standing by to provide
                one-on-one support. Customers can reach out with any questions,
                concerns, or requests and let us do what we do best.
              </p>
            </FadeIn>
          </div>

          <div className="col-span-3 mt-16 lg:mt-0 " aria-hidden="true">
            <FadeIn delay={0.2}>
              <img
                src="/img/chat.svg"
                className="h-auto md:h-full w-auto rounded-lg shadow-xl mx-auto"
                aria-hidden="true"
                alt="Support chat messages"
              />
            </FadeIn>
          </div>
        </div>
      </div>

      <div id="contact-us" className="bg-gray-50">
        <div className="max-w-7xl mx-auto py-8 lg:py-20 px-4 sm:px-6 lg:pg-8">
          <FadeIn>
            <h2 className="text-5xl text-alt font-extrabold my-7 text-center">
              Become an embedded <span className="text-primary">partner</span>
            </h2>
          </FadeIn>
          <p className="mt-4 text-lg leading-6 text-gray-500 text-center">
            Tell us more about your interest in becoming a Coverdash embedded
            partner!
          </p>
          <div className="max-w-xl mx-auto mt-12">
            <ContactUs page="partner" />
          </div>
        </div>
      </div>

      <BottomGraphic />
      <Footer />
    </>
  )
}

export default Partner
